<template>
  <div class="feedback-form">
    <a href="" @click.prevent="open">{{ title }}</a>
    <transition name="fade">
      <div class="veil" v-if="opened" @click="close"></div>
    </transition>
    <transition name="slide">
      <div :class="['side-panel', {'side-panel_opened': opened}]" v-if="opened">
        <div class="side-panel__inner side-panel__inner_no-margin">
          <span class="side-panel__close" @click="close"></span>
          <form class="form" action="" @submit.prevent="submit" ref="form">
            <h2 class="form__title">{{ title }}</h2>
            <template v-if="formSent">
              <p class="form__success-note" v-html="tlSuccessMessage" />
            </template>
            <template v-else>
              <div class="form__row">
                <div class="form__field">
                  <span class="form__field-label">{{ tlEmailLabel }}</span>
                  <input
                    type="text"
                    :class="['form__textinput', {'form__textinput_error': $v.form.email.$error}]"
                    name="email"
                    v-model="form.email"
                    :placeholder="tlEmailPlaceholder"
                  />
                  <span class="form__field-error" v-if="$v.form.email.$error && !$v.form.email.required">{{ tlErrorLabel }}</span>
                  <span class="form__field-error" v-if="$v.form.email.$error && !$v.form.email.email">{{ tlEmailError }}</span>
                </div>
              </div>
              <div class="form__row">
                <div class="form__field">
                  <span class="form__field-label">{{ tlTextLabel }}</span>
                  <textarea
                    name="text"
                    v-model="form.text"
                    :class="['form__textarea', {'form__textarea_error': $v.form.text.$error}]"
                    :placeholder="tlTextPlaceholder"
                  ></textarea>
                  <span class="form__field-error" v-if="$v.form.text.$error && !$v.form.text.required">{{ tlErrorLabel }}</span>
                  <span class="form__field-error" v-if="$v.form.text.$error && !$v.form.text.minLength && $v.form.text.required">
                    {{ tlMinLengthLabel }} - {{ minLengthText }}
                  </span>
                </div>
              </div>
              <div class="form__captcha">
                <div class="form__field">
                  <div class="form__captcha-actions g-visible-mobile">
                    <a href="" @click.prevent="getCaptcha">{{ tlCaptchaReset }}</a>
                  </div>
                  <span class="form__field-label">{{ tlCaptchaLabel }}</span>
                  <input type="hidden" name="captcha-sid" :value="captchaSid">
                  <input
                    type="text"
                    name="captcha"
                    :class="['form__textinput', {'form__textinput_error': $v.form.captcha.$error}]"
                    v-model="form.captcha"
                  />
                  <span class="form__field-error" v-if="$v.form.captcha.$error">{{ tlErrorLabel }}</span>
                  <div class="form__captcha-actions g-hidden-mobile">
                    <a href="" @click.prevent="getCaptcha">{{ tlCaptchaReset }}</a>
                  </div>
                </div>
                <div class="form__captcha-image">
                  <img :src="captchaSrc" alt="code">
                </div>
              </div>
              <input
                type="submit"
                :value="tlBtnLabel"
                class="button button_black button_wide"
              >
              <div class="form__error-message form__error-message_small-margin" v-if="showError">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20ZM9 7V5H11V7H9ZM9 15V9H11V15H9Z" fill="#292929"/>
                </svg>
                {{ errorText }}
              </div>
            </template>
          </form>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {email, minLength, required} from "vuelidate/lib/validators";
import axios from 'axios';

export default {
  name: 'FeedbackForm',
  props: {
    title: {
      type: String,
      default: 'link'
    },
    actionCaptcha: {
      type: String,
      default: '',
    },
    actionFeedback: {
      type: String,
      default: '',
    },
    tlEmailLabel: {
      type: String,
      default: '',
    },
    tlEmailPlaceholder: {
      type: String,
      default: '',
    },
    tlEmailError: {
      type: String,
      default: '',
    },
    tlTextLabel: {
      type: String,
      default: '',
    },
    tlTextPlaceholder: {
      type: String,
      default: '',
    },
    tlCaptchaLabel: {
      type: String,
      default: '',
    },
    tlCaptchaReset: {
      type: String,
      default: '',
    },
    tlBtnLabel: {
      type: String,
      default: '',
    },
    tlErrorLabel: {
      type: String,
      default: '',
    },
    tlMinLengthLabel: {
      type: String,
      default: '',
    },
    tlSuccessMessage: {
      type: String,
      default: '',
    }
  },
  data() {
    return {
      opened: false,
      captchaSrc: '',
      captchaSid: '',
      showError: false,
      errorText: '',
      formSent: false,
      form: {
        email: '',
        text: '',
        captcha: '',
      },
      minLengthText: 20
    }
  },
  validations() {
    return {
      form: {
        email: {required, email},
        text: {required, minLength: minLength(this.minLengthText)},
        captcha: {required},
      }
    }
  },
  methods: {
    open() {
      this.opened = true;
      this.getCaptcha();
    },
    close() {
      this.opened = false;
      this.formSent = false;
    },
    getFormData() {
      return new FormData(this.$refs.form);
    },
    async submit() {
      this.showError = false;
      this.$v.$touch();
      if (!this.$v.$invalid) {
        try {
          const response = await axios({
            url: this.actionFeedback,
            method: 'POST',
            data: this.getFormData(),
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });

          if (response.data.status === 'error') {
            throw new Error(response.data.errors[0].message);
          }

          if (response.data.status === 'success') {
            this.formSent = true;
          }
        } catch (error) {
          this.showError = true;
          this.errorText = error.message;
          this.getCaptcha();
          this.form.captcha = '';
        }
      }
    },
    async getCaptcha() {
      try {
        const response = await axios.get(this.actionCaptcha);
        this.captchaSrc = response.data.data.src;
        this.captchaSid = response.data.data.sid;
      }
      catch (error) {
        console.log(error);
      }
    }
  }
}
</script>

<style lang="scss">
@import "../scss/base/includes";

.feedback-form {
  .form__textarea {
    resize: none;
    outline: none;
    -moz-appearance: none;
    scrollbar-width: thin;

    &::-webkit-scrollbar-button {
      background-image: none;
      background-repeat: no-repeat;
      width: 4px;
      height: 4px;
    }
    &::-webkit-scrollbar-track {
      background-color: rgba($dark, 0.1);
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 4px;
      background-color: $dark;
    }
    &::-webkit-resizer {
      background-image: none;
      background-repeat: no-repeat;
      width: 4px;
      height: 4px;
      display: none;
    }
    &::-webkit-scrollbar {
      width: 4px;
      height: 4px;
    }

    @media screen and (-webkit-min-device-pixel-ratio:0) {
      scrollbar-width: auto; /* Для браузеров, поддерживающих -webkit */
    }
  }
}
</style>
